import type { StripeSchoolLicence } from '@kimuno/api-sdk';
import { format as formatDate } from 'date-fns';
import { de } from 'date-fns/locale';

import { subscriptionDateFormat } from '../constants';

// The status, date and product rules of a Stripe school's licence, shared by the
// admin dashboard (Lizenzübersicht) and the teacher's card in Mein Abo so both
// views describe the same licence the same way. The seats response is a
// structural superset of `StripeSchoolLicence`, so the dashboard passes it as is.

type LicenceStatus = 'active' | 'cancelled' | 'scheduled';

export const STATUS_CHIPS: Record<
	LicenceStatus,
	{ label: string; color: 'success' | 'warning' | 'info' }
> = {
	active: { label: 'Active', color: 'success' },
	cancelled: { label: 'Cancelled', color: 'warning' },
	scheduled: { label: 'Scheduled', color: 'info' },
};

interface DateCard {
	title: string;
	date: string;
}

export const deriveIncludedProducts = (licence: StripeSchoolLicence): string[] => {
	const included = ['Bibliothek'];
	if (licence.mukiEnabled === true) included.push('AI');
	if (licence.packageKey === 'kombi') included.push('Course Seats');
	return included;
};

export const deriveLicenceStatus = (licence: StripeSchoolLicence): LicenceStatus => {
	if (licence.subscriptionState?.reason === 'notYetStarted') return 'scheduled';
	// A school trial is created with `cancel_at = trial_end`, so a trialing
	// subscription's `cancelAt` is its trial end, not a cancellation.
	if (licence.subscriptionState?.status === 'trialing') return 'active';
	return licence.cancelAt ? 'cancelled' : 'active';
};

export const deriveDateCards = (
	licence: StripeSchoolLicence,
	status: LicenceStatus
): DateCard[] => {
	const isTrialing = licence.subscriptionState?.status === 'trialing';
	const cards: { title: string; date: string | null | undefined }[] =
		status === 'scheduled'
			? [{ title: 'Start date', date: licence.subscriptionState?.willStartAt }]
			: [
					{ title: isTrialing ? 'Trial period started on' : 'Start date', date: licence.startedAt },
					status === 'cancelled'
						? { title: 'End date', date: licence.cancelAt }
						: {
								title: isTrialing ? 'Trial period ends at' : 'Next renewal Date',
								date: licence.periodEnd,
							},
				];

	// A date the response doesn't carry (the apiv3 proxy strips undeclared
	// fields) hides its card rather than showing an empty one.
	return cards.filter((card): card is DateCard => Boolean(card.date));
};

export const formatSubscriptionDate = (iso: string) =>
	formatDate(new Date(iso), subscriptionDateFormat, { locale: de });
